import { Cow, Truck, NotePencil, Share, FilePdf, PencilSimple } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatBRL, formatNum, formatDate, downloadPdf, shareTransactionPdf, toastWithQuota } from "@/lib/gado";

function DetailRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-gado-line/60 last:border-0">
      <span className="text-[11px] uppercase tracking-wider text-gado-muted shrink-0">{label}</span>
      <span className="text-sm font-medium text-gado-ink text-right min-w-0 break-words tabular-nums">{value}</span>
    </div>
  );
}

/**
 * Janela de detalhes da pesagem — compartilhada pelo Dashboard (Pesagens recentes)
 * e pelo Histórico (clique no card).
 */
export function TransactionDetailsDialog({ tx, open, onClose, onEdit }) {
  if (!tx) return null;

  const share = () => {
    toastWithQuota(shareTransactionPdf(tx), {
      loading: "Preparando PDF…",
      success: (r) => {
        if (r.shared) return "Compartilhado";
        if (r.cancelled) return "Compartilhamento cancelado";
        return "PDF baixado";
      },
      error: "Falha ao gerar PDF",
    });
  };

  const download = () => {
    const name = `Pesagem_${(tx.buyer_name || "Gado").replace(/\s+/g, "_")}_${formatDate(tx.date).replace(/\//g, "-")}.pdf`;
    toastWithQuota(downloadPdf(tx.transaction_id, name), {
      loading: "Gerando PDF…",
      success: "PDF baixado",
      error: "Falha ao gerar PDF",
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tx-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gado-ink">
            <Cow size={20} weight="fill" className="text-gado-green" />
            {tx.buyer_name || "Pesagem"}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 text-xs">
            <span>{formatDate(tx.date)}</span>
            <span className="text-gado-line">·</span>
            <span>{tx.animal_type || "—"}</span>
            {tx.status === "em_andamento" && (
              <Badge variant="outline" className="border-gado-gold/40 bg-gado-gold/10 text-gado-gold text-[10px] ml-auto">
                Em andamento
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Highlighted totals — estilo RESUMO + TOTAL A PAGAR do PDF */}
        <div className="grid grid-cols-2 gap-2 text-center pt-1">
          <div className="rounded-lg bg-gado-bg/60 py-3 px-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-gado-muted">Cabeças</p>
            <p className="font-mono font-bold text-gado-ink text-base sm:text-lg tabular-nums truncate">{formatNum(tx.total_quantity, 0)}</p>
          </div>
          <div className="rounded-lg bg-gado-bg/60 py-3 px-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-gado-muted">Arrobas</p>
            <p className="font-mono font-bold text-gado-ink text-base sm:text-lg tabular-nums truncate">{formatNum(tx.total_arrobas, 1)}@</p>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-gado-gold/10 border border-gado-gold/25 px-3.5 py-3">
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-gado-ink shrink-0">
            Total a pagar
          </span>
          <span
            data-testid="tx-details-total"
            className="font-mono text-base sm:text-lg font-bold text-gado-gold tabular-nums text-right min-w-0 truncate"
          >
            {formatBRL(tx.total_amount)}
          </span>
        </div>

        {/* Detail rows */}
        <div className="pt-1">
          <DetailRow label="Peso total bruto" value={`${formatNum(tx.total_weight, 1)} kg`} />
          <DetailRow label="Média por cabeça" value={`${formatNum(tx.avg_weight, 1)} kg`} />
          <DetailRow label="Média de arrobas" value={`${formatNum(tx.avg_arrobas, 2)} @`} />
          <DetailRow
            label={tx.pricing_mode === "kg" ? "Valor por kg" : "Valor por @"}
            value={formatBRL(tx.pricing_mode === "kg" && tx.price_per_kg ? tx.price_per_kg : tx.price_per_arroba)}
          />
          <DetailRow label="Valor por animal" value={formatBRL(tx.unit_price_per_animal)} />
          {tx.discount_kg > 0 && <DetailRow label="Desconto cedido" value={`${formatNum(tx.discount_kg, 0)} kg`} />}
          <DetailRow label="Cidade / Povoado" value={tx.city_district} />
          <DetailRow label="Ferro do gado" value={tx.iron_brand} />
          <DetailRow label="Parceiro" value={tx.partner_name} />
        </div>

        {(tx.transport_driver_name || tx.transport_plate || tx.transport_freight_value) && (
          <div className="rounded-lg bg-gado-bg/50 p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gado-gold font-bold">
              <Truck size={12} /> Transporte
            </div>
            {tx.transport_driver_name && <p className="text-xs text-gado-ink">{tx.transport_driver_name}</p>}
            {(tx.transport_plate || tx.transport_phone) && (
              <p className="text-xs text-gado-muted">
                {tx.transport_plate} {tx.transport_phone && ` · ${tx.transport_phone}`}
              </p>
            )}
            {tx.transport_freight_value > 0 && (
              <p className="text-xs text-gado-ink">Frete: <b>{formatBRL(tx.transport_freight_value)}</b></p>
            )}
          </div>
        )}

        {(tx.bank_data || tx.observations) && (
          <div className="rounded-lg bg-gado-bg/50 p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gado-gold font-bold">
              <NotePencil size={12} /> Observações
            </div>
            {tx.bank_data && <p className="text-xs text-gado-inkSoft whitespace-pre-wrap">{tx.bank_data}</p>}
            {tx.observations && <p className="text-xs text-gado-inkSoft whitespace-pre-wrap">{tx.observations}</p>}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            onClick={share}
            data-testid="tx-details-share"
            variant="outline"
            className="border-gado-green/40 text-gado-green hover:bg-gado-green/5"
          >
            <Share size={16} className="mr-1.5" /> Compartilhar
          </Button>
          <Button
            onClick={download}
            data-testid="tx-details-pdf"
            variant="outline"
          >
            <FilePdf size={16} className="mr-1.5" /> PDF
          </Button>
          <Button
            onClick={onEdit}
            data-testid="tx-details-edit"
            className="bg-gado-green hover:bg-gado-greenHover text-white"
          >
            <PencilSimple size={16} className="mr-1.5" /> Editar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
